<template>
  <div
    class="rechercheViewWrapper"
    ref="rechercheViewWrapper"
  >
    <div class="searchWrapper searchWrapper--desktop">
      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        class="searchInput"
        size="large"
        state="default"
        placeholder="Numéro, SIRET..."
        :maxlength="80"
        deletable
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
      />
      <div class="searchWrapper--actions">
        <WrapPermissions
          :permissions="[...Dispositif]"
          tool-tip-position="left"
          v-slot="{ isNotAllowed }"
        >
          <BasicButton
            @click="addDemande"
            class="button-right"
            label="Créer un nouveau dossier"
            size="large"
            :disabled="isNotAllowed"
          />
        </WrapPermissions>
      </div>
    </div>
    <div class="searchWrapper searchWrapper--mobile">
      <WrapPermissions
        :permissions="[...Dispositif]"
        tool-tip-position="top"
        v-slot="{ isNotAllowed }"
      >
        <BasicButton
          @click="addDemande"
          label="Créer un nouveau dossier"
          icon-name="folder-add"
          size="large"
          width="full"
          :disabled="isNotAllowed"
        />
      </WrapPermissions>
      <BasicInput
        v-model="search"
        iconName="search"
        iconState="iconLeft"
        size="large"
        state="default"
        placeholder="Numéro, stagiaire, SIRET..."
        :maxlength="80"
      />
      <BasicButton
        @click="showFilters = !showFilters"
        :active="showFilters"
        variant="ghost"
        label="Recherche Avancée"
        iconName="filter"
        size="large"
        width="full"
      />
    </div>
    <Transition>
      <div
        class="filtersWrapper"
        v-if="showFilters"
      >
        <WrapperDropdownMultiple
          v-model="statuts"
          label="Statut"
          :items="statutsItems"
          placeholder="Sélection"
          class="col-12 col-md-18 col-sm-36"
        />

        <div class="col-4 col-sm-36 actions--item">
          <BasicTooltip label="Ajouter les filtres aux favoris">
            <BasicButton
              :disabled="!searchIsFiltered"
              type="secondary"
              iconName="star"
              @click="addFavori"
            />
          </BasicTooltip>
          <BasicTooltip label="Supprimer les filtres">
            <BasicButton
              @click="removeFilters"
              :disabled="!searchIsFiltered"
              type="reverse"
              iconName="filter-remove"
              style="display: inline"
            />
          </BasicTooltip>
        </div>
      </div>
    </Transition>
    <div class="informationWrapper">
      <BasicPagination
        :nbPages="paginationNumberOfPages"
        :nbPagesMax="10"
        :currentPage="pageNumber"
        :nbResults="store.pageData?.totalRecords"
        @change="pageChange"
      />
      <BasicDropdown
        v-model="pageSize"
        :items="pageSizeItems"
        placeholder="Nombre de résultats"
        width="auto"
        force-value
      />
    </div>
    <div
      class="listComponentMainWrapper"
      ref="listComponentMainWrapper"
    >
      <div class="mobileWrapper">
        <DemandeCardMobile
          v-for="item in store.pageData?.data"
          :key="item?.id"
          :item
        />
      </div>
      <div class="desktopWrapper">
        <DemandeHeader v-model="order" />
        <DemandeCardDesktop
          v-for="item in store.pageData?.data"
          class="listCardDesktopWrapper"
          :key="item?.id"
          :item
        />
      </div>
      <cards-layout
        v-if="!store.pageData?.data.length"
        cardsType="Brouillons"
        headerText="Brouillons"
        :hasHeader="false"
        :cards="[]"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { RechercheDemandeQueryDTO } from '@/features/api/extranet/dto/Demande/Recherche'
  import { usePreferencesUtilisateur } from '@/features/application/preferences'
  import { PREFERENCES_UTILISATEUR_APP, type PreferenceGlobale } from '@/features/application/types'
  import { StatutAdherentLibelle } from '@/features/domaine/types/opco/Demande/TT'
  import { Dispositif } from '@/features/domaine/types/opco/shared/Dispositif'
  import { useImmerFieldPatcher } from '@/features/interface/composables'
  import CardsLayout from '@/features/interface/home/tableauDeBord/cardsLayout/CardsLayout.vue'
  import WrapPermissions from '@/features/interface/shared/wrap-permissions/WrapPermissions.vue'
  import { isNotNullUndefinedOrEmptyString, isObjectEquals } from '@/features/shared/tools'
  import { formatSearchQuery, parseSearchQuery } from '@/features/shared/tools/parse'
  import { navigateTo, type NavigateToQueryDemandeFormation, type WithNavigateToLocation } from '@/router/navigateTo'
  import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
  import { onBeforeRouteUpdate } from 'vue-router'
  import { objectToDropdownItems } from '../../shared/tools'
  import { pageSizeItems } from '../dropdown-items'
  import type { PagedRequest } from '../storeRechercheBuilder'
  import { DemandeCardDesktop, DemandeCardMobile, DemandeHeader } from './components'
  import { useRechercheDemandeTTActions } from './useRechercheDemandeTTActions.ts'
  import { useRechercheDemandeTTStore } from './useRechercheDemandeTTStore.ts'

  const props = defineProps<WithNavigateToLocation<NavigateToQueryDemandeFormation>>()

  const store = useRechercheDemandeTTStore()
  onBeforeUnmount(store.$reset)
  const prefService = usePreferencesUtilisateur<PreferenceGlobale>(PREFERENCES_UTILISATEUR_APP)
  const { addFavori, addDemande } = useRechercheDemandeTTActions()
  const showFilters = ref(false)
  const statutsItems = objectToDropdownItems(StatutAdherentLibelle)

  const { setupFieldWithKey, setupFieldWithOptions, setupCustomUpdater } = useImmerFieldPatcher<
    PagedRequest<RechercheDemandeQueryDTO>
  >({
    getModel: () => store.pageRequest!,
    setModel(newModel) {
      console.log(newModel)
      const queryChanged = !isObjectEquals(store.pageRequest?.query, newModel.query)
      if (queryChanged) {
        newModel = {
          ...newModel,
          pagination: {
            pageNumber: 1,
            pageSize: newModel.pagination.pageSize,
          },
        }
      }

      navigateTo({
        ...props.navigateToLocation,
        ...newModel,
      })
    },
    debounceDelay: 1000,
  })

  onBeforeRouteUpdate((to, ...args) => {
    return to.meta.navigateToHelper?.beforeEnter?.call(null, to, ...args)
  })

  const search = setupFieldWithOptions<string>({
    getValue: () => formatSearchQuery(store.pageRequest?.query.search ?? []),
    setPatch: (patch, value) => {
      console.log(patch, value)
      if (!value) patch.query.search = []
      else patch.query.search = parseSearchQuery(value)
    },
    withDebounce: true,
  })

  const dispositifs = setupFieldWithKey<string[]>('query.dispositifs')
  const statuts = setupFieldWithKey<string[]>('query.statuts')
  const subrogation = setupFieldWithKey<boolean | null | undefined>('query.avecSubrogation')
  const mandat = setupFieldWithKey<boolean | null | undefined>('query.estSousMandatGestion')
  const pageNumber = setupFieldWithKey<number>('pagination.pageNumber')
  //const periodeItems = objectToDropdownItems(PeriodeLibelle)
  const periode = setupFieldWithKey<string | null | undefined>('query.periode')

  const pageSize = setupFieldWithOptions<number | null | undefined>({
    getValue: (model) => model.pagination?.pageSize,
    setPatch: (patch, value) => {
      console.log(patch, value)
      patch.pagination = {
        pageNumber: 1,
        pageSize: value!,
      }
      prefService.setPreferencesAsync({ pageSize: value! })
    },
  })

  const order = setupFieldWithOptions<string>({
    getValue: () => {
      return store.pageRequest?.orderBy?.at(0) ?? ''
    },
    setPatch: (patch, value) => {
      patch.orderBy = value ? [value] : []
      patch.pagination = {
        pageNumber: 1,
        pageSize: patch.pagination.pageSize,
      }
    },
  })

  const searchIsFiltered = computed(
    () =>
      search.value.length ||
      dispositifs.value.length ||
      statuts.value.length ||
      mandat.value != null ||
      subrogation.value != null ||
      periode.value != null,
  )

  const paginationNumberOfPages = computed(() => {
    if (store?.pageData?.totalRecords) {
      return Math.ceil(store?.pageData?.totalRecords / store.pageRequest!.pagination!.pageSize!)
    }
    return 0
  })

  onBeforeMount(() => {
    if (
      dispositifs.value.length ||
      statuts.value.length ||
      mandat.value != null ||
      subrogation.value != null ||
      isNotNullUndefinedOrEmptyString(periode.value)
    ) {
      showFilters.value = true
    }
  })

  const pageChange = (page: number) => {
    pageNumber.value = page
  }

  const removeFilters = setupCustomUpdater((pageRequest) => {
    pageRequest.query = {
      search: undefined,
      estSousMandatGestion: undefined,
      avecSubrogation: undefined,
      dispositifs: undefined,
      periode: undefined,
    }
    showFilters.value = false
  })
</script>

<style scoped>
  @import '../index.less';
</style>
